(() => {
  const COMPONENT_SELECTOR = '[data-brandadox-whatsapp-order]';
  const initializedComponents = new WeakSet();

  class BrandadoxWhatsAppOrder {
    constructor(container) {
      this.container = container;
      this.productInfo = container.closest('product-info') || document;
      this.button = container.querySelector('[data-brandadox-whatsapp-link]');
      this.phone = container.dataset.whatsappNumber || '';
      this.data = this.readProductData();

      if (!this.button || !this.data) return;

      this.handleSelectionChange = () => requestAnimationFrame(() => this.update());
      this.productInfo.addEventListener('change', this.handleSelectionChange);
      this.productInfo.addEventListener('input', this.handleSelectionChange);
      this.button.addEventListener('click', () => this.update());

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, () => this.update());
        this.unsubscribeQuantity = subscribe(PUB_SUB_EVENTS.quantityUpdate, () => this.update());
      }

      this.update();
    }

    readProductData() {
      const dataElement = this.container.querySelector('[data-brandadox-product-order-data]');
      if (!dataElement) return null;

      try {
        return JSON.parse(dataElement.textContent);
      } catch (error) {
        console.error('Brandadox WhatsApp order data could not be read.', error);
        return null;
      }
    }

    getSelectedVariant() {
      const variantInputs = [...this.productInfo.querySelectorAll('input[name="id"]')];
      const variantId =
        variantInputs.map((input) => Number(input.value)).find((id) => this.data.variants.some((variant) => variant.id === id)) ||
        Number(new URLSearchParams(window.location.search).get('variant')) ||
        Number(this.data.selectedVariantId);

      return (
        this.data.variants.find((variant) => variant.id === variantId) ||
        this.data.variants.find((variant) => variant.available) ||
        this.data.variants[0]
      );
    }

    getQuantity() {
      const quantityInput = this.productInfo.querySelector('.quantity__input[name="quantity"]');
      const quantity = Number.parseInt(quantityInput?.value || '1', 10);
      return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    }

    formatMoney(cents) {
      const amount = Number(cents || 0) / 100;

      try {
        return new Intl.NumberFormat('en-IN', {
          style: 'currency',
          currency: this.data.currency || 'INR',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(amount);
      } catch (_error) {
        return `₹${amount.toFixed(2)}`;
      }
    }

    getOptionDetails(variant) {
      const optionNames = Array.isArray(this.data.optionNames) ? this.data.optionNames : [];
      const optionValues = Array.isArray(variant.options) ? variant.options : [];
      const details = optionValues
        .map((value, index) => ({
          name: optionNames[index] || `Option ${index + 1}`,
          value,
        }))
        .filter((option) => option.value && option.value !== 'Default Title');

      const sizeOption = details.find((option) => option.name.toLowerCase().includes('size'));
      return { details, sizeOption };
    }

    buildMessage(variant, quantity) {
      const unitPrice = Number(variant.price || 0);
      const totalPrice = unitPrice * quantity;
      const { details, sizeOption } = this.getOptionDetails(variant);
      const variantUrl = `${this.data.url}${this.data.url.includes('?') ? '&' : '?'}variant=${variant.id}`;
      const lines = [
        'Hello Brandadox, I would like to order this product:',
        '',
        `Product: ${this.data.title}`,
      ];

      if (sizeOption) {
        lines.push(`Selected size: ${sizeOption.value}`);
      }

      details
        .filter((option) => option !== sizeOption)
        .forEach((option) => lines.push(`${option.name}: ${option.value}`));

      lines.push(
        `Quantity: ${quantity}`,
        `Unit price: ${this.formatMoney(unitPrice)}`,
        `Total: ${this.formatMoney(totalPrice)}`,
        `Product link: ${variantUrl}`
      );

      if (!variant.available) {
        lines.push('Availability: Please confirm stock');
      }

      return lines.join('\n');
    }

    update() {
      const variant = this.getSelectedVariant();
      if (!variant) return;

      const quantity = this.getQuantity();
      const message = this.buildMessage(variant, quantity);
      this.button.href = `https://wa.me/${this.phone}?text=${encodeURIComponent(message)}`;
      this.button.dataset.orderMessage = message;
      this.button.classList.toggle('is-unavailable', !variant.available);
    }
  }

  const initialize = (root = document) => {
    if (root.matches?.(COMPONENT_SELECTOR) && !initializedComponents.has(root)) {
      initializedComponents.add(root);
      new BrandadoxWhatsAppOrder(root);
    }

    root.querySelectorAll(COMPONENT_SELECTOR).forEach((container) => {
      if (initializedComponents.has(container)) return;
      initializedComponents.add(container);
      new BrandadoxWhatsAppOrder(container);
    });
  };

  initialize();
  document.addEventListener('product-info:loaded', (event) => initialize(event.target));

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) initialize(node);
      });
    });
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
